import { Component, OnInit, OnDestroy, EventEmitter, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { QuickcoachImportService, QuickCoachLoginState, LoginResult } from '../../../services/quickcoach-import.service';
import { Feedback, showErrorToast } from 'src/app/decorators/feedback-decorator';
import { BulkCreateCustomerRequest } from '@me-fit-mono/typings';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-quickcoach-import-tool',
  templateUrl: './quickcoach-import-tool.component.html',
  styleUrls: ['./quickcoach-import-tool.component.scss']
})
export class QuickcoachImportToolComponent implements OnInit, OnDestroy {
  emailForm: FormGroup = this.formBuilder.group({
    email: ['', [Validators.required, Validators.email]]
  })

  codeForm: FormGroup = this.formBuilder.group({
    code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
  })

  loginState: QuickCoachLoginState = {
    step: 'email',
  };

  isLoading = false;

  importedCustomersPayload: BulkCreateCustomerRequest;

  @Output() onImportDone = new EventEmitter();

  constructor(
    private formBuilder: FormBuilder,
    private quickcoachService: QuickcoachImportService,
    private $translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.initializeConnection();
  }

  @Feedback({
    i18nLoadingMessage: 'quickcoach.loading.customers',
    i18nFailKey: 'quickcoach.loading.customers.fail'
  })
  async loadCustomers() {
    const userIds = this.loginState.userData?.fields.userIds || [];
    const customers = await this.quickcoachService.getCustomers(userIds);

    console.log('Imported Customers:', customers);

    this.importedCustomersPayload = customers.map((customer) => {
      return {
        firstName: customer.profile.firstName,
        lastName: customer.profile.lastName,
        email: customer.profile.email,
        phoneNumber: customer.profile.phone,
        notes: customer.notes.map(note => ({
          text: note.content,
          isEditable: false,
          createdAt: new Date(note.createdAt).toISOString()
        }))
      }
    })
  }

  ngOnDestroy(): void {
    // Clean up connection (but keep token stored)
    this.quickcoachService.disconnect();
  }

  private async initializeConnection(): Promise<void> {
    try {
      this.isLoading = true;
      await this.quickcoachService.initializeConnection();

      // Try to login with stored token
      await this.tryStoredTokenLogin();
    } catch (error) {
      console.error('Failed to initialize connection:', error);
      showErrorToast({
        message: this.$translate.instant('quickcoach.connection.failed'),
        duration: 10000,
        buttons: [{
          text: this.$translate.instant('action.retry'),
          handler: () => {
            this.onStartOver();
          }
        }]
      });
      this.loginState.step = 'error';
    } finally {
      this.isLoading = false;
    }
  }

  private async tryStoredTokenLogin(): Promise<void> {
    try {
      const loginResult = await this.quickcoachService.tryLoginWithStoredToken();

      if (loginResult) {
        console.log('Successfully logged in with stored token');

        // Wait for user data
        const userData = await this.quickcoachService.waitForUserData();

        this.loginState = {
          step: 'success',
          loginToken: loginResult.token,
          userData
        };
      }
    } catch (error) {
      console.error('Failed to login with stored token:', error);
      // Continue with normal flow if stored token login fails
    }
  }

  async onEmailSubmit(): Promise<void> {
    if (this.emailForm.invalid) {
      return;
    }

    const email = this.emailForm.get('email')?.value;

    try {
      this.isLoading = true;


      const result = await this.quickcoachService.startLoginFlow(email);

      this.loginState = {
        step: 'code',
        email,
        userId: result.userId
      };

    } catch (error: any) {
      console.error('Email login failed:', error);
      showErrorToast({
        message: this.$translate.instant('quickcoach.login.fail')
      })
    } finally {
      this.isLoading = false;
    }
  }

  async onCodeSubmit(): Promise<void> {
    if (this.codeForm.invalid || !this.loginState.userId) {
      return;
    }

    const code = this.codeForm.get('code')?.value;

    try {
      this.isLoading = true;

      const loginResult: LoginResult = await this.quickcoachService.verifyLoginCode(
        this.loginState.userId,
        code
      );

      // Wait for user data
      const userData = await this.quickcoachService.waitForUserData();

      this.loginState = {
        step: 'success',
        email: this.loginState.email,
        userId: this.loginState.userId,
        loginToken: loginResult.token,
        userData: userData
      };

    } catch (error: any) {
      console.error('Code verification failed:', error);
      showErrorToast({
        message: this.$translate.instant('quickcoach.invalid.code')
      })
    } finally {
      this.isLoading = false;
    }
  }

  onBackToEmail(): void {
    this.loginState = {
      step: 'email'
    };
    this.codeForm.reset();
  }

  onStartOver(): void {
    this.loginState = {
      step: 'email'
    };
    this.emailForm.reset();
    this.codeForm.reset();
    this.initializeConnection();
  }

  onLogout(): void {
    this.quickcoachService.logout();
    this.onStartOver();
  }

  async checkUserByEmail(email: string): Promise<void> {
    try {
      const userInfo = await this.quickcoachService.getUserByEmailPublic(email);
      console.log('User found:', userInfo);
    } catch (error) {
      console.error('Error checking user by email:', error);
    }
  }

  // Helper method to set the demo code
  setDemoCode(): void {
    this.codeForm.patchValue({ code: '695291' });
  }

  onCustomersImported(): void {
    // Emit the event to notify parent component that import is done
    this.onImportDone.emit();
  }
}
